import type { UnitSystem } from "~/api/products/types";
import { splitAllowanceUnits } from "~/lib/sale-engine";
import { employeeActions } from "~/providers/store/employee";
import { autoId } from "~/utils/auto-id";

import {
  REWARDS_CATEGORY_ID,
  type BasketDiscount,
  type BasketItem,
  type CapturedWeight,
  type KioskSession,
  type VariantChoice
} from "./types";
import { INITIAL_STATE, kioskSessionStore } from "./store";
import { orderMinimumBlockActions } from "./order-minimum-block";
import { getDisplayProductBasketDiscount } from "./hooks/use-kiosk-products/get-display-product-basket-discount";
import { type KioskDisplayProductList } from "./hooks/use-kiosk-products/types";

function sessionCreate({ locale }: { locale: KioskSession["locale"] }) {
  kioskSessionStore.setState(() => {
    return {
      orderId: autoId(),
      serviceId: autoId(10),
      basket: [],
      childId: null,
      diningOption: null,
      locale,
      shopperName: null,
      postCheckoutTextInputs: null,
      lastUpdatedAt: Date.now(),
      redeemingSessionStatus: "idle",
      activeCategoryId: null
    } satisfies KioskSession;
  });
}

function sessionReset() {
  kioskSessionStore.setState(() => {
    return INITIAL_STATE;
  });
  // The identify-user flow also writes the scanned user into the persisted
  // employee store; leaving it behind attributes the next customer's order
  // to the previous user.
  employeeActions.reset();
  // A checkout block left behind by a guest who walked away would otherwise
  // greet the next guest on the start screen.
  orderMinimumBlockActions.clear();
}

type AddToBasketArgs = {
  quantity?: number;
  choices?: VariantChoice[];
  weight?: CapturedWeight;
  /** Overrides the discount; defaults to the product's first discount. */
  discount?: BasketDiscount;
};

type AddUnitsArgs = {
  productId: string;
  type: BasketItem["type"];
  quantity: number;
  discount?: BasketDiscount;
  choices?: VariantChoice[];
  weight?: CapturedWeight;
  unitSystem?: Exclude<UnitSystem, "piece">;
};

function addUnits({
  productId,
  quantity,
  discount,
  type,
  choices,
  weight,
  unitSystem
}: AddUnitsArgs) {
  kioskSessionStore.setState((session) => {
    if (!session) {
      return;
    }

    const index = session.basket.findIndex((prod) => {
      if (type === "weighted") return false;
      if (prod.type !== type) return false;
      if (prod.discounts[0]?.id !== discount?.id) return false;

      if (type === "variant" && prod.type === "variant" && choices) {
        return (
          prod.productId === productId &&
          choices.every((choice) =>
            prod.choices.some(
              (prodChoice) =>
                prodChoice.id === choice.id &&
                prodChoice.quantity === choice.quantity &&
                prodChoice.variantId === choice.variantId
            )
          )
        );
      }

      return prod.productId === productId;
    });

    if (index !== -1) {
      session.basket[index].quantity += quantity;
      return;
    }

    const discounts: BasketDiscount[] = [];
    if (discount) {
      discounts.push(discount);
    }

    let item: BasketItem = {
      id: autoId(),
      type: "regular",
      productId,
      quantity,
      discounts
    };

    if (type === "variant") {
      item = {
        ...item,
        choices: choices ?? [],
        type: "variant"
      };
    }

    if (type === "weighted" && weight !== undefined && unitSystem) {
      item = {
        ...item,
        type: "weighted",
        weight: weight.weight,
        weightUnit: weight.unit,
        weightDecimals: weight.decimals,
        unitSystem
      };
    }

    session.basket.push(item);
  });
}

function basketAdd(
  item: KioskDisplayProductList,
  {
    quantity = 1,
    choices,
    weight,
    discount: discountOverride
  }: AddToBasketArgs = {}
) {
  const { discount, allowanceCap } = getDisplayProductBasketDiscount(
    item,
    discountOverride
  );

  const type = item.product.type;
  const unitSystem =
    item.product.type === "weighted" ? item.product.unitSystem : undefined;

  const { discountedQuantity, fullQuantity } = splitAllowanceUnits(
    quantity,
    allowanceCap
  );

  const base = {
    productId: item.product.id,
    type,
    choices,
    weight,
    unitSystem
  };

  if (discountedQuantity > 0) {
    addUnits({ ...base, quantity: discountedQuantity, discount });
  }
  if (fullQuantity > 0) {
    addUnits({ ...base, quantity: fullQuantity, discount: undefined });
  }
}

// Past a numberOfProducts cap, the extra unit spills to a full-price sibling.
function basketIncrement(itemId: string, allowanceCap?: number) {
  if (splitAllowanceUnits(1, allowanceCap).fullQuantity === 0) {
    kioskSessionStore.setState((session) => {
      if (!session) {
        return;
      }

      const index = session.basket.findIndex((item) => item.id === itemId);
      if (index !== -1) {
        session.basket[index].quantity += 1;
      }
    });
    return;
  }

  const item = kioskSessionStore
    .getState()
    ?.basket.find((basketItem) => basketItem.id === itemId);
  if (!item) {
    return;
  }

  addUnits({
    productId: item.productId,
    type: item.type,
    quantity: 1,
    discount: undefined,
    choices: item.type === "variant" ? item.choices : undefined,
    weight:
      item.type === "weighted"
        ? {
            weight: item.weight,
            unit: item.weightUnit,
            decimals: item.weightDecimals
          }
        : undefined,
    unitSystem: item.type === "weighted" ? item.unitSystem : undefined
  });
}

function basketDecrement(itemId: string) {
  kioskSessionStore.setState((session) => {
    if (!session) {
      return;
    }

    const index = session.basket.findIndex((item) => item.id === itemId);
    if (index !== -1) {
      if (session.basket[index].quantity === 1) {
        session.basket.splice(index, 1);
      } else {
        session.basket[index].quantity -= 1;
      }
    }
  });
}

function basketRemove(itemId: string) {
  kioskSessionStore.setState((session) => {
    if (!session) {
      return;
    }

    const index = session.basket.findIndex((item) => item.id === itemId);
    if (index !== -1) {
      session.basket.splice(index, 1);
    }
  });
}

function basketReset() {
  kioskSessionStore.setState((session) => {
    if (!session) {
      return;
    }

    session.basket = [];
  });
}

function setDiningOption(diningOption: KioskSession["diningOption"]) {
  kioskSessionStore.setState((session) => {
    if (!session) {
      return;
    }

    session.diningOption = diningOption;
  });
}

function setShopperName(shopperName: KioskSession["shopperName"]) {
  kioskSessionStore.setState((session) => {
    if (!session) {
      return;
    }

    session.shopperName = shopperName;
  });
}

function setPostCheckoutTextInput(key: string, value: string) {
  kioskSessionStore.setState((session) => {
    if (!session) {
      return;
    }

    if (!session.postCheckoutTextInputs) {
      session.postCheckoutTextInputs = {};
    }

    session.postCheckoutTextInputs[key] = value;
  });
}

function setLocale(locale: KioskSession["locale"]) {
  kioskSessionStore.setState((session) => {
    if (!session) {
      return;
    }

    session.locale = locale;
  });
}

function setUserChildId(childId: KioskSession["childId"]) {
  kioskSessionStore.setState((session) => {
    if (!session) {
      return;
    }

    session.childId = childId;
  });
}

function resetOrderId() {
  const orderId = autoId();

  kioskSessionStore.setState((session) => {
    if (!session) {
      return;
    }

    session.orderId = orderId;
  });
}

function generateServiceId() {
  const serviceId = autoId(10);

  kioskSessionStore.setState((session) => {
    if (!session) {
      return;
    }

    session.serviceId = serviceId;
  });

  return serviceId;
}

function setRedeemingSessionStatus(
  status: KioskSession["redeemingSessionStatus"]
) {
  kioskSessionStore.setState((session) => {
    if (!session) {
      return;
    }

    session.redeemingSessionStatus = status;
    if (status === "active") {
      session.activeCategoryId = REWARDS_CATEGORY_ID;
    }
  });
}

function setActiveCategoryId(categoryId: KioskSession["activeCategoryId"]) {
  kioskSessionStore.setState((session) => {
    if (!session) {
      return;
    }

    session.activeCategoryId = categoryId;
  });
}

export const kioskActions = {
  session: {
    resetOrderId,
    start: sessionCreate,
    reset: sessionReset,
    generateServiceId,
    setDiningOption,
    setShopperName,
    setPostCheckoutTextInput,
    setLocale,
    setRedeemingSessionStatus,
    setActiveCategoryId
  },

  user: {
    setChildId: setUserChildId
  },

  basket: {
    add: basketAdd,
    increment: basketIncrement,
    decrement: basketDecrement,
    remove: basketRemove,
    reset: basketReset
  }
};
